// ── Length caps ─────────────────────────────────────────────────────────────
export const MAX_NAME_LENGTH = 100;
export const MAX_DESCRIPTION_LENGTH = 1000;

// ── Bad-word list ────────────────────────────────────────────────────────────
// List of blocked words. Extend as needed.
const BAD_WORDS: string[] = [
  "fuck", "fucker", "fucking", "motherfuck", "motherfucker",
  "shit", "shitty", "bullshit",
  "ass", "asshole", "dumbass", "jackass",
  "bitch", "bitchy",
  "bastard",
  "cock", "dick", "prick",
  "pussy", "cunt",
  "whore", "slut",
  "faggot", "fag",
  "retard",
  "wank", "wanker",
  "arse", "arsehole", "tosser", "twat",
  "nigger", "nigga",
];

/**
 * Normalises leet-speak substitutions so "f4ck" and "fu(k" are still caught.
 */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[@4]/g, "a")
    .replace(/3/g, "e")
    .replace(/[!1]/g, "i")
    .replace(/0/g, "o")
    .replace(/[$5]/g, "s")
    .replace(/[()[\]{}]/g, "");
}

/**
 * Returns true if `text` contains any word from BAD_WORDS.
 * Checks whole-word matches to avoid false-positives (e.g. "class" ≠ "ass").
 */
export function containsBadWords(text: string): boolean {
  if (!text || !text.trim()) return false;
  const normalized = normalize(text);
  return BAD_WORDS.some((word) =>
    new RegExp(`(?<![a-z])${word}(?![a-z])`, "i").test(normalized)
  );
}

/**
 * Validates a single field for inappropriate language.
 * Returns `{ valid: true }` if clean, or `{ valid: false, message }` if flagged.
 */
export function validateInput(
  text: string,
  fieldName = "Input"
): { valid: boolean; message: string } {
  if (!text || !text.trim()) return { valid: true, message: "" };
  if (containsBadWords(text)) {
    return {
      valid: false,
      message: `${fieldName} contains inappropriate language. Please use professional language.`,
    };
  }
  return { valid: true, message: "" };
}

/**
 * Validates multiple fields at once.
 * Returns the first violation found, or `{ valid: true }` if all pass.
 */
export function validateFields(
  fields: { value: string; name: string }[]
): { valid: boolean; message: string } {
  for (const field of fields) {
    const result = validateInput(field.value, field.name);
    if (!result.valid) return result;
  }
  return { valid: true, message: "" };
}

// ── Query / text sanitisation ────────────────────────────────────────────────

/**
 * Sanitises a search query: trims whitespace, strips HTML tags and characters
 * that could cause XSS. Returns an empty string if blank after sanitisation.
 */
export function sanitizeQuery(query: string): string {
  if (!query) return "";
  return query
    .trim()
    .replace(/<[^>]*>/g, "")      // strip HTML tags
    .replace(/[<>&"'`]/g, "")     // strip remaining dangerous HTML chars
    .slice(0, 500);               // hard cap to prevent oversized payloads
}

/**
 * Validates a (already sanitised) search query.
 * Pass `strict = true` to also block bad words.
 */
export function validateSearchQuery(
  query: string,
  strict = false
): { valid: boolean; message?: string } {
  if (!query || !query.trim()) {
    return { valid: false, message: "Search query cannot be empty." };
  }
  if (strict && containsBadWords(query)) {
    return { valid: false, message: "Search query contains inappropriate language." };
  }
  return { valid: true };
}

/**
 * Sanitises a free-text field: trims, strips HTML, and caps at `maxLength`.
 */
export function sanitizeText(text: string, maxLength: number): string {
  if (!text) return "";
  return text
    .trim()
    .replace(/<[^>]*>/g, "")      // strip HTML tags
    .replace(/[<>&"'`]/g, "")     // strip dangerous HTML chars
    .slice(0, maxLength);
}

/**
 * Validates a funding target string (must be empty or a positive number).
 * Returns `{ valid: true }` if blank (field is optional).
 */
export function validateFundingTarget(value: string): { valid: boolean; error?: string } {
  if (!value || !value.trim()) return { valid: true };
  const num = parseFloat(value.replace(/[,$\s]/g, ""));
  if (isNaN(num)) {
    return { valid: false, error: "Funding target must be a valid number." };
  }
  if (num <= 0) {
    return { valid: false, error: "Funding target must be a positive number." };
  }
  if (num > 1_000_000_000_000) {
    return { valid: false, error: "Funding target value seems unrealistically large." };
  }
  return { valid: true };
}

// ── Email & password ─────────────────────────────────────────────────────────

/**
 * Validates an email address: checks format and flags inappropriate language
 * in the local part (before the @).
 * Returns `{ valid: true }` or `{ valid: false, error: string }`.
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  if (!email || !email.trim()) {
    return { valid: false, error: "Email is required." };
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { valid: false, error: "Please enter a valid email address." };
  }
  const localPart = email.split("@")[0];
  if (containsBadWords(localPart)) {
    return { valid: false, error: "Email contains inappropriate language." };
  }
  return { valid: true };
}

/**
 * Validates password strength.
 * Returns an array of error strings (empty array = valid).
 */
export function validatePassword(password: string): string[] {
  const errors: string[] = [];
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }
  return errors;
}
